import React from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DatePickerProps {
  label?: string;
  value?: string | Date | null;
  onChange?: (date: string | Date | null) => void;
  min?: string;
  max?: string;
  error?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  useReactDatePicker?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  error,
  className = '',
  required = false,
  disabled = false,
  placeholder = 'Select date...',
  useReactDatePicker = true
}) => {
  const inputClasses = `w-full rounded-lg border ${
    error ? 'border-[#dc3545]' : 'border-[#3d4d61]'
  } bg-[#1c2127] px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-[#137fec] cursor-pointer ${
    disabled ? 'opacity-50 cursor-not-allowed' : ''
  }`;

  const handleDateChange = (date: Date | null) => {
    if (onChange) {
      onChange(date);
    }
  };

  const dateValue = typeof value === 'string' && value ? new Date(value) : value as Date | null;
  const minDate = min ? new Date(min) : undefined;
  const maxDate = max ? new Date(max) : undefined;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="mb-2 block text-sm font-medium text-white">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {useReactDatePicker ? (
          <ReactDatePicker
            selected={dateValue}
            onChange={handleDateChange}
            minDate={minDate}
            maxDate={maxDate}
            disabled={disabled}
            placeholderText={placeholder}
            dateFormat="MM/dd/yyyy"
            className={inputClasses}
            wrapperClassName="w-full"
            calendarClassName="dark-calendar"
          />
        ) : (
          <input
            type="date"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange?.(e.target.value)}
            min={min}
            max={max}
            disabled={disabled}
            className={inputClasses}
          />
        )}
        <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-[#9dabb9] pointer-events-none">
          calendar_today
        </span>
      </div>
      {error && (
        <p className="mt-1 text-sm text-[#dc3545] flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">error</span>
          {error}
        </p>
      )}
    </div>
  );
};

interface DateRangePickerProps {
  startLabel?: string;
  endLabel?: string;
  startValue: string | Date | null;
  endValue: string | Date | null;
  onStartChange: (date: string | Date | null) => void;
  onEndChange: (date: string | Date | null) => void;
  required?: boolean;
  startError?: string;
  endError?: string;
  disabled?: boolean;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startLabel = 'Start Date',
  endLabel = 'End Date',
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  required = false,
  startError,
  endError,
  disabled = false
}) => {
  return (
    <div className="grid grid-cols-2 gap-4">
      <DatePicker
        label={startLabel}
        value={startValue}
        onChange={onStartChange}
        required={required}
        error={startError}
        max={typeof endValue === 'string' ? endValue : undefined}
        disabled={disabled}
        placeholder="Start date"
      />
      <DatePicker
        label={endLabel}
        value={endValue}
        onChange={onEndChange}
        required={required}
        error={endError}
        min={typeof startValue === 'string' ? startValue : undefined}
        disabled={disabled}
        placeholder="End date"
      />
    </div>
  );
};

